/**
 * Agent Client Library
 * Provides a simple interface for discovering and executing agents from other tabs
 * Extended with Volcano SDK support
 */

import axios from 'axios';
import type {
  VolcanoWorkflow,
  ExecutionProgress,
  ProviderStats,
  McpServer,
  ExecutionStreamEvent,
  VolcanoApiResponse,
} from '../features/agents/volcano/types/volcano.types';

export interface AgentInfo {
  id: string;
  name: string;
  description: string;
  domain: string;
  agent_type: 'internal' | 'app';
  capabilities: string[];
  operations: OperationSchema[];
  version: string;
  deployment_stage: string;
  execution_engine?: 'volcano' | 'legacy';
  execution_type?: string;
  volcano_workflow?: VolcanoWorkflow;
  rate_limit?: {
    requests_per_minute: number;
    requests_per_hour: number;
  };
}

export interface OperationSchema {
  operation: string;
  description: string;
  input_schema?: Record<string, any>;
  output_schema?: Record<string, any>;
  examples?: Array<{
    name: string;
    input: Record<string, any>;
    output: Record<string, any>;
  }>;
}

export interface ExecuteResult {
  success: boolean;
  result: any;
  metadata?: {
    agent_id: string;
    operation: string;
    duration_ms: number;
  };
  error?: string;
}

/**
 * Agent Client for discovering and executing agents
 */
export class AgentClient {
  private baseUrl: string;
  private discoveryCache: Map<string, { data: AgentInfo[]; timestamp: number }>;
  private cacheTimeout: number = 5 * 60 * 1000; // 5 minutes

  constructor(baseUrl?: string) {
    // Auto-detect base URL based on environment
    if (baseUrl) {
      this.baseUrl = baseUrl;
    } else if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      this.baseUrl = 'http://localhost:8090'; // agent-registry
    } else {
      this.baseUrl = `${window.location.origin}/cp/api/agents`; // production via Kong
    }

    this.discoveryCache = new Map();
  }

  /**
   * Discover agents based on filters
   */
  async discoverAgents(filters?: {
    type?: 'internal' | 'app';
    domain?: string;
    deployment_stage?: string;
    status?: string;
  }): Promise<AgentInfo[]> {
    try {
      const cacheKey = JSON.stringify(filters || {});
      const cached = this.discoveryCache.get(cacheKey);

      // Return cached data if still valid
      if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
        return cached.data;
      }

      // Fetch from API
      const params = new URLSearchParams();
      if (filters?.type) params.append('type', filters.type);
      if (filters?.domain) params.append('domain', filters.domain);
      if (filters?.deployment_stage) params.append('deployment_stage', filters.deployment_stage);
      if (filters?.status) params.append('status', filters.status);

      const response = await axios.get(`${this.baseUrl}/discover?${params.toString()}`);
      const agents = response.data.agents as AgentInfo[];

      // Cache the results
      this.discoveryCache.set(cacheKey, {
        data: agents,
        timestamp: Date.now()
      });

      return agents;
    } catch (error: any) {
      console.error('Agent discovery failed:', error);
      throw new Error(`Failed to discover agents: ${error.message}`);
    }
  }

  /**
   * Get operations for a specific agent
   */
  async getAgentOperations(agentId: string): Promise<OperationSchema[]> {
    try {
      const response = await axios.get(`${this.baseUrl}/discover/${agentId}/operations`);
      return response.data.operations as OperationSchema[];
    } catch (error: any) {
      console.error(`Failed to get operations for agent ${agentId}:`, error);
      throw new Error(`Failed to get operations: ${error.message}`);
    }
  }

  /**
   * Execute an agent operation
   */
  async executeAgent(
    agentId: string,
    operation: string,
    data: any
  ): Promise<ExecuteResult> {
    try {
      // Validate operation exists (fail-fast)
      const operations = await this.getAgentOperations(agentId);
      const operationSchema = operations.find(op => op.operation === operation);

      if (!operationSchema) {
        throw new Error(
          `Operation ${operation} not found for agent ${agentId}. Available operations: ${operations.map(op => op.operation).join(', ')}`
        );
      }

      // Execute via agent-executor
      const executorUrl = this.getExecutorUrl();
      const response = await axios.post(
        `${executorUrl}/agents/dynamic/${agentId}/execute`,
        {
          operation,
          data
        }
      );

      return {
        success: true,
        result: response.data.result,
        metadata: {
          agent_id: agentId,
          operation,
          duration_ms: response.data.metadata?.duration_ms || 0
        }
      };
    } catch (error: any) {
      console.error(`Agent execution failed (${agentId}.${operation}):`, error);

      // Return structured error instead of throwing (fail-fast but informative)
      return {
        success: false,
        result: null,
        error: error.response?.data?.message || error.message
      };
    }
  }

  /**
   * Get available domains with agent counts
   */
  async getAvailableDomains(): Promise<Array<{
    domain: string;
    agent_count: number;
    agents: Array<{ id: string; name: string; agent_type: string }>;
  }>> {
    try {
      const response = await axios.get(`${this.baseUrl}/discover/domains`);
      return response.data.domains;
    } catch (error: any) {
      console.error('Failed to get available domains:', error);
      throw new Error(`Failed to get domains: ${error.message}`);
    }
  }

  /**
   * Validate operation input against schema
   */
  async validateOperationInput(
    agentId: string,
    operation: string,
    input: any
  ): Promise<{ valid: boolean; errors?: string[] }> {
    try {
      const response = await axios.post(`${this.baseUrl}/discover/validate-operation`, {
        agent_id: agentId,
        operation,
        input
      });

      return {
        valid: response.data.valid,
        errors: response.data.errors || []
      };
    } catch (error: any) {
      console.error('Operation input validation failed:', error);
      return {
        valid: false,
        errors: [error.response?.data?.message || error.message]
      };
    }
  }

  /**
   * Clear discovery cache
   */
  clearCache(): void {
    this.discoveryCache.clear();
  }

  // ============================================================================
  // Volcano-Specific Methods
  // ============================================================================

  /**
   * Execute a Volcano workflow
   */
  async executeVolcanoWorkflow(
    agentId: string,
    input: any,
    context?: Record<string, any>
  ): Promise<VolcanoApiResponse<ExecutionProgress>> {
    try {
      const volcanoUrl = this.getVolcanoUrl();
      const response = await axios.post(`${volcanoUrl}/execute`, {
        agent_id: agentId,
        input,
        context,
      });

      return {
        success: true,
        data: response.data as ExecutionProgress,
        metadata: {
          execution_id: response.data.execution_id,
          duration_ms: response.data.total_duration_ms,
          cost: response.data.total_cost,
        },
      };
    } catch (error: any) {
      console.error(`Volcano workflow execution failed (${agentId}):`, error);
      return {
        success: false,
        error: error.response?.data?.error || error.message,
      };
    }
  }

  /**
   * Stream Volcano workflow execution via Server-Sent Events
   * Returns an EventSource for real-time updates
   */
  streamVolcanoExecution(
    agentId: string,
    input: any,
    context?: Record<string, any>,
    onEvent?: (event: ExecutionStreamEvent) => void,
    onError?: (error: Error) => void,
    onComplete?: () => void
  ): EventSource {
    const volcanoUrl = this.getVolcanoUrl();
    
    // Encode parameters for SSE endpoint
    const params = new URLSearchParams({
      agent_id: agentId,
      input: JSON.stringify(input),
      ...(context && { context: JSON.stringify(context) }),
    });

    const eventSource = new EventSource(`${volcanoUrl}/execute/stream?${params.toString()}`);

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data) as ExecutionStreamEvent;
        onEvent?.(data);

        // Close connection on complete or error
        if (data.type === 'complete' || data.type === 'error') {
          eventSource.close();
          onComplete?.();
        }
      } catch (error) {
        console.error('Failed to parse SSE event:', error);
      }
    };

    eventSource.onerror = (event) => {
      console.error('SSE connection error:', event);
      eventSource.close();
      onError?.(new Error('SSE connection failed'));
    };

    return eventSource;
  }

  /**
   * Get provider status and costs
   */
  async getProviderStatus(): Promise<ProviderStats | null> {
    try {
      const volcanoUrl = this.getVolcanoUrl();
      const response = await axios.get(`${volcanoUrl}/providers`);
      return response.data as ProviderStats;
    } catch (error: any) {
      console.error('Failed to get provider status:', error);
      return null;
    }
  }

  /**
   * Get registered MCP servers
   */
  async getMcpServers(): Promise<McpServer[]> {
    try {
      const volcanoUrl = this.getVolcanoUrl();
      const response = await axios.get(`${volcanoUrl}/mcp/servers`);
      return response.data.servers as McpServer[];
    } catch (error: any) {
      console.error('Failed to get MCP servers:', error);
      return [];
    }
  }

  /**
   * Register a new MCP server
   */
  async registerMcpServer(name: string, url: string): Promise<boolean> {
    try {
      const volcanoUrl = this.getVolcanoUrl();
      await axios.post(`${volcanoUrl}/mcp/servers`, { name, url });
      return true;
    } catch (error: any) {
      console.error('Failed to register MCP server:', error);
      return false;
    }
  }

  /**
   * Remove an MCP server
   */
  async removeMcpServer(name: string): Promise<boolean> {
    try {
      const volcanoUrl = this.getVolcanoUrl();
      await axios.delete(`${volcanoUrl}/mcp/servers/${name}`);
      return true;
    } catch (error: any) {
      console.error('Failed to remove MCP server:', error);
      return false;
    }
  }

  /**
   * Convert a legacy agent to Volcano workflow
   */
  async convertLegacyToVolcano(agentId: string): Promise<VolcanoApiResponse<VolcanoWorkflow>> {
    try {
      const volcanoUrl = this.getVolcanoUrl();
      const response = await axios.post(`${volcanoUrl}/convert-legacy`, {
        agent_id: agentId,
      });

      return {
        success: true,
        data: response.data.volcano_workflow as VolcanoWorkflow,
      };
    } catch (error: any) {
      console.error('Failed to convert legacy agent:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message,
      };
    }
  }

  /**
   * Get Volcano engine health status
   */
  async getVolcanoHealth(): Promise<{
    healthy: boolean;
    version?: string;
    uptime?: number;
  }> {
    try {
      const volcanoUrl = this.getVolcanoUrl();
      const response = await axios.get(`${volcanoUrl}/health`);
      return {
        healthy: response.status === 200,
        ...response.data,
      };
    } catch (error) {
      return { healthy: false };
    }
  }

  /**
   * Update agent with new Volcano workflow
   */
  async updateAgentWorkflow(
    agentId: string,
    workflow: VolcanoWorkflow
  ): Promise<boolean> {
    try {
      const response = await axios.put(`${this.baseUrl}/${agentId}`, {
        volcano_workflow: workflow,
        execution_engine: 'volcano',
      });
      
      // Clear cache after update
      this.clearCache();
      
      return response.status === 200;
    } catch (error: any) {
      console.error('Failed to update agent workflow:', error);
      return false;
    }
  }

  /**
   * Get executor URL based on environment
   */
  private getExecutorUrl(): string {
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return 'http://localhost:8092'; // agent-executor
    } else {
      return `${window.location.origin}/api/agent-executor`; // production via Kong
    }
  }

  /**
   * Get Volcano engine URL based on environment
   */
  private getVolcanoUrl(): string {
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return 'http://localhost:8000/api/volcano'; // via Kong in dev
    } else {
      return `${window.location.origin}/api/volcano`; // production via Kong
    }
  }
}

/**
 * Singleton instance for convenience
 */
export const agentClient = new AgentClient();

/**
 * React hook for using agent client
 */
export function useAgentClient() {
  return agentClient;
}

/**
 * Ask agent coordinator (legacy function for AskAIPanel)
 */
export async function askAgent(question: string, domain: string) {
  try {
    const response = await axios.post('/api/agent-coordinator/ask', {
      question,
      domain,
    });
    return response.data;
  } catch (error: any) {
    console.error('Ask agent failed:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message,
    };
  }
}
